// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Grouping data centers that share a location (or sit close enough to
// overlap on screen) into a single marker.
//
// Two data centers at one site — GA-10 and its mock twin, both in Marietta —
// would otherwise draw one marker exactly on top of another. The one
// underneath is then unreachable: not hoverable, not clickable, and
// invisible. That is the same class of bug as dropping an unplaceable site,
// so markers are clustered and the cluster offers a list to choose from.

import type { Point } from './projection';

export interface Clusterable {
  siteId: string;
  point: Point;
}

export interface Cluster<T extends Clusterable> {
  /** Stable across renders: the member site ids, sorted and joined. */
  id: string;
  /** Where to draw the marker — the mean of its members' points. */
  point: Point;
  members: T[];
}

/**
 * Group points that fall within `threshold` of each other.
 *
 * Single-link (transitive) grouping: A joins B's cluster if it is close to
 * *any* member, not just the first. That matters because the alternative —
 * comparing against a fixed seed — can leave two markers overlapping simply
 * because of the order the sites arrived in.
 *
 * O(n²), which is the right trade for fleet-sized inputs (tens of sites);
 * a spatial index here would be more code and more risk for no gain.
 *
 * `threshold` is in geometry units and is expected to be the marker's
 * on-screen diameter at the current zoom, so points cluster exactly when
 * they would visually collide.
 */
export function clusterByProximity<T extends Clusterable>(
  items: readonly T[],
  threshold: number
): Cluster<T>[] {
  const groups: T[][] = [];

  for (const item of items) {
    const hit = groups.find(group =>
      group.some(member => distance(member.point, item.point) <= threshold)
    );
    if (hit) hit.push(item);
    else groups.push([item]);
  }

  return groups.map(members => ({
    id: members
      .map(m => m.siteId)
      .sort()
      .join('|'),
    point: centroid(members.map(m => m.point)),
    members,
  }));
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function centroid(points: readonly Point[]): Point {
  const sum = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
  return { x: sum.x / points.length, y: sum.y / points.length };
}
